import React, { FC, useCallback, useContext } from 'react';

import Plus from '@gravity-ui/icons/Plus';
import Xmark from '@gravity-ui/icons/Xmark';
import { Button, Icon, Loader, RadioButton, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { routes } from 'client/lib/routes';
import { getCanEditAsAdmin } from 'client/utils';
import { hasSpecialChars } from 'client/utils/settings/common';
import * as appKeyset from 'i18n/AppsIndex';
import * as keyset from 'i18n/FeatureFlags';

import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { PageLoader } from '../Loader/PageLoader';
import { LinkMobile } from '../MenuMobile/LinkMobile';

import styles from './FeatureFlags.module.scss';

const t = i18n(keyset);
const tApp = i18n(appKeyset);

const BOOLEAN_FLAGS = [
    { value: 'True', content: 'True' },
    { value: 'False', content: 'False' },
];

const SkillFlagInput: FC<{
    index: number;
    onInputChange: (flagIndex: number, updatedFlag: string | null) => void;
    value?: string;
    disabled: boolean;
}> = (props) => {
    const { index, onInputChange, value, disabled } = props;
    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const text = e.currentTarget.value;
            if (hasSpecialChars(text)) return;
            onInputChange(index, text);
        },
        [index, onInputChange],
    );
    return <TextInput value={value} onChange={onChange} size="l" disabled={disabled} />;
};

const UserFlagKeyInput: FC<{
    index: number;
    flagValue: boolean;
    onUpdateUserFlag: (flagIndex: number, updatedFlagKey: string | null, updatedFlagValue: boolean) => void;
    value?: string;
    disabled: boolean;
}> = (props) => {
    const { index, flagValue, onUpdateUserFlag, value, disabled } = props;
    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const text = e.currentTarget.value;
            if (hasSpecialChars(text)) return;
            onUpdateUserFlag(index, text, flagValue);
        },
        [index, flagValue, onUpdateUserFlag],
    );
    return <TextInput value={value} onChange={onChange} size="l" disabled={disabled} />;
};

export const FeatureFlags: React.FC = () => {
    const context = useContext(CurrentAliceAppContext);
    const { isLoading, skillFlags, addSkillFlag, updateSkillFlag, userFlags, addUserFlag, updateUserFlag, saveFlags } =
        useFeatureFlags();

    const disabled = isLoading || !getCanEditAsAdmin();

    if (!context?.aliceApp) {
        return <PageLoader />;
    }

    return (
        <>
            <LinkMobile
                to={routes.app.appPage(context.aliceApp.channel, true, 'settings', context.aliceApp.id)}
                text={tApp('main-information')}
            />
            <LinkMobile
                to={routes.app.installableAppSettings(
                    context.aliceApp.channel,
                    true,
                    'feature-flags',
                    context.aliceApp.id,
                )}
                text={tApp('feature-flags')}
                isCurrent
            />
            <header className={styles.header}>
                <div>
                    <h1 className={styles.title_main}>{context?.aliceApp?.draft?.name}</h1>
                </div>
                <Button
                    className={styles.saveButton}
                    onClick={saveFlags}
                    view="action"
                    loading={isLoading}
                    disabled={disabled}
                >
                    {t('save-flags')}
                </Button>
            </header>
            <div className={styles.content}>
                <h3 className={styles.sectionDescription}>{t('app-flags-subtitle')}</h3>
                {isLoading ? (
                    <div className={styles.loaderContainer}>
                        <Loader size="l" />
                    </div>
                ) : (
                    <>
                        <h2 className={styles.title_section}> {t('title-app-flags')} </h2>
                        {skillFlags.map((flag, index) => (
                            <div key={String(index)} className={styles.formRow}>
                                <SkillFlagInput
                                    value={flag}
                                    index={index}
                                    onInputChange={updateSkillFlag}
                                    disabled={disabled}
                                />
                                <div>
                                    <Button onClick={() => updateSkillFlag(index, null)} size="l" disabled={disabled}>
                                        <Icon data={Xmark} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                        <div>
                            <Button className={styles.addButton} onClick={addSkillFlag} disabled={disabled}>
                                <Icon data={Plus} />
                            </Button>
                        </div>

                        <h2 className={styles.title_section}> {t('title-user-feature-flags')} </h2>
                        {userFlags.map(({ key, value }, index) => (
                            <div key={index} className={styles.formRow}>
                                <UserFlagKeyInput
                                    value={key}
                                    index={index}
                                    flagValue={value}
                                    onUpdateUserFlag={updateUserFlag}
                                    disabled={disabled}
                                />
                                <RadioButton
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const updatedValue = e.currentTarget.value;
                                        updateUserFlag(index, key, updatedValue === 'True');
                                    }}
                                    defaultValue={BOOLEAN_FLAGS[0].value}
                                    options={BOOLEAN_FLAGS}
                                    value={value ? 'True' : 'False'}
                                    size="l"
                                    disabled={disabled}
                                />
                                <Button onClick={() => updateUserFlag(index, null, value)} size="l" disabled={disabled}>
                                    <Icon data={Xmark} />
                                </Button>
                            </div>
                        ))}
                        <div>
                            <Button className={styles.addButton} onClick={addUserFlag} disabled={disabled}>
                                <Icon data={Plus} />
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};
